import { useEffect, useRef, useState, useCallback } from 'react';

const WS_URL = process.env.REACT_APP_WS_URL || 'wss://tv.gkmmedia.com';

export const useWebSocket = () => {
  const ws = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [streamStats, setStreamStats] = useState({});
  const [streamUpdates, setStreamUpdates] = useState({});
  const reconnectTimeout = useRef(null);
  const isConnecting = useRef(false);

  const connect = useCallback(() => {
    // Prevent multiple simultaneous connection attempts
    if (isConnecting.current || (ws.current && ws.current.readyState === WebSocket.OPEN)) {
      return;
    }
    
    try {
      isConnecting.current = true;
      
      // Use wss for HTTPS, ws for HTTP
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/ws`;
      
      console.log('Connecting to WebSocket:', wsUrl);
      ws.current = new WebSocket(wsUrl);

      ws.current.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        isConnecting.current = false;
        
        // Subscribe to all streams (admin view) or specific streams
        sendMessage({
          type: 'subscribe_all'
        });
      };

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleMessage(data);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.current.onclose = (event) => {
        console.log('WebSocket disconnected', event.code, event.reason);
        setIsConnected(false);
        isConnecting.current = false;
        
        // Clear any existing reconnect timeout
        if (reconnectTimeout.current) {
          clearTimeout(reconnectTimeout.current);
        }
        
        // Reconnect after 5 seconds (only if not a normal close)
        if (event.code !== 1000 && event.code !== 1001) {
          reconnectTimeout.current = setTimeout(() => {
            console.log('Attempting to reconnect...');
            connect();
          }, 5000);
        }
      };

      ws.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        isConnecting.current = false;
      };
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      isConnecting.current = false;
    }
  }, []);

  const handleMessage = useCallback((data) => {
    switch (data.type) {
      case 'connected':
        console.log('WebSocket server says:', data.message);
        break;
        
      case 'subscribed':
        // Handle subscription confirmation
        console.log('WebSocket subscription confirmed:', data.message || data.streams || 'Subscribed');
        break;
        
      case 'stream_stats':
        // Global stats update
        if (data.data) {
          setStreamStats(data.data);
        }
        break;
        
      case 'stream_update':
        // Individual stream update
        if (data.streamKey) {
          setStreamUpdates(prev => ({
            ...prev,
            [data.streamKey]: {
              ...data.data,
              timestamp: data.timestamp
            }
          }));
        }
        break;
        
      case 'stream_status_change':
        // Stream went live or offline
        console.log(`Stream ${data.streamKey} is now ${data.isLive ? 'LIVE' : 'OFFLINE'}`);
        break;
        
      case 'pong':
        // Heartbeat response
        break;
        
      default:
        console.log('Unknown WebSocket message type:', data.type);
    }
  }, []);

  const sendMessage = useCallback((message) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(message));
    }
  }, []);

  const subscribeToStreams = useCallback((streamIds) => {
    sendMessage({
      type: 'subscribe',
      streamIds: streamIds
    });
  }, [sendMessage]);

  useEffect(() => {
    connect();
    
    // Heartbeat to keep connection alive
    const heartbeat = setInterval(() => {
      if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        sendMessage({ type: 'ping' });
      }
    }, 30000);

    return () => {
      clearInterval(heartbeat);
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
      if (ws.current) {
        // Use code 1000 for normal closure
        ws.current.close(1000, 'Component unmounting');
      }
    };
  }, [connect, sendMessage]);

  return {
    isConnected,
    streamStats,
    streamUpdates,
    subscribeToStreams,
    sendMessage
  };
};

export default useWebSocket;
